/* eslint-disable @typescript-eslint/member-ordering */

import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, Signal, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, UrlSegment } from '@angular/router';
import { AuthService } from '@libs/shared/auth';
import { SpinnerOverlayService } from '@libs/shared/common';
import { CustomerModel } from '@libs/account/models';
import { DeferredPageInfoTracking, TrackingGoogleService } from '@libs/shared/tracking';
import { combineLatest, firstValueFrom, Observable } from 'rxjs';

import { CustomerAutocompleteComponent } from '../autocomplete';

@Component({
  selector: 'ui-customer-navigation',
  templateUrl: './customer-navigation.component.html',
  styleUrls: ['./customer-navigation.component.scss'],
  imports: [
    CustomerAutocompleteComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerNavigationComponent implements OnInit {
  private readonly router: Router = inject(Router);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly authService: AuthService = inject(AuthService);
  private readonly trackingService: TrackingGoogleService = inject(TrackingGoogleService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly spinnerOverlayService: SpinnerOverlayService = inject(SpinnerOverlayService);
  private readonly deferredPageInfoTracking: DeferredPageInfoTracking = inject(DeferredPageInfoTracking);

  public readonly customers: Signal<CustomerModel[] | undefined> = this.authService.allCustomers;
  public readonly selectedCustomer: WritableSignal<CustomerModel | undefined> = signal(undefined);

  private readonly customers$: Observable<CustomerModel[] | undefined> = toObservable(this.customers);

  public ngOnInit(): void {
    if (!this.authService.customersLoaded()) {
      this.spinnerOverlayService.showLoader();
      void this.authService.loadCustomers()
        .finally(() => {
          void this.spinnerOverlayService.hideLoader();
          this.reportPageInfoWithoutCustomers();
        });
    }

    if (!this.route.firstChild) {
      this.deferredPageInfoTracking.complete();

      return;
    }

    combineLatest([
      this.route.firstChild.paramMap,
      this.customers$,
    ]).pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(([params, customers]) => {
      if (!customers) {
        return;
      }

      const selectedCustomerId: string | null = params.get('customerId');
      const currentCustomer: CustomerModel | undefined = this.authService.currentCustomer();
      const selectedCustomer: CustomerModel | undefined =
        customers.find(customer => customer.id === selectedCustomerId)
        ?? currentCustomer
        ?? customers[0];

      if (selectedCustomerId !== selectedCustomer.id) {
        // The pageInfo of the url without the customer id is left unreported: the redirect replaces it.
        this.navigateToCustomer(selectedCustomer, true);
      } else {
        this.deferredPageInfoTracking.complete();
      }

      this.selectedCustomer.set(selectedCustomer);
    });
  }

  public navigateToCustomer(customer: CustomerModel | undefined, isRedirect: boolean = false): void {
    if (!customer || (!isRedirect && customer.id === this.selectedCustomer()?.id)) {
      return;
    }

    const url$: Observable<UrlSegment[]> | undefined = this.route.firstChild?.url;

    if (!url$) {
      return;
    }

    void firstValueFrom(url$).then(segments => {
      this.trackingService.trackAccountEvent('kundendaten', 'kundennummer-wechseln');

      return this.router.navigate(
        ['account', segments[0].path, customer.id],
        { replaceUrl: isRedirect },
      );
    });
  }

  /** Without customers there is nobody to redirect to, so the page stays on the url it was opened with. */
  private reportPageInfoWithoutCustomers(): void {
    if (!this.customers()?.length) {
      this.deferredPageInfoTracking.complete();
    }
  }
}
